import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api';
import { useAuth, useLocale } from '../../context';
import { getPromoPageHtml } from '../promos/promoUtils';
import {
  formatTournamentCountdown,
  getTournamentEndMs,
  getTournamentStartMs,
  getTournamentPhase,
  parseLeaderboardBonus,
} from '../promos/tournamentTime';
import {
  IconClock,
  IconCoin,
  IconFsStar,
  IconGift,
  IconParticipants,
  IconPlaces,
  RankHexagon,
} from '../promos/TournamentIcons';

function IconHelp() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm0 14.25a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8Zm0-9.75a3.375 3.375 0 0 0-3.375 3.375.75.75 0 0 0 1.5 0 1.875 1.875 0 1 1 1.875 1.875.75.75 0 0 0-.75.75v1.5a.75.75 0 0 0 1.5 0v-.878A3.376 3.376 0 0 0 12 6.75Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M.456.454a1.094 1.094 0 0 1 1.548 0L9 7.468 16 .454A1.094 1.094 0 0 1 17.546 2l-7.014 6.998 7.015 6.998a1.087 1.087 0 0 1 0 1.545l-.002.003a1.09 1.09 0 0 1-1.545 0l-6.999-7.016-6.998 7.015v.001a1.09 1.09 0 0 1-1.545 0l-.003-.003a1.087 1.087 0 0 1 0-1.544l.001-.001 7.015-6.998L.456 2a1.094 1.094 0 0 1 0-1.547"
      />
    </svg>
  );
}

function maskParticipant(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'ID ****';
  const bare = raw.replace(/^ID\s+/i, '');
  if (bare.includes('*')) {
    const starred = bare.match(/^(\d{2})\*{3,4}(\d{2})$/);
    if (starred) return `ID ${starred[1]}****${starred[2]}`;
    return `ID ${bare}`;
  }
  if (/^\d+$/.test(bare) && bare.length >= 4) {
    return `ID ${bare.slice(0, 2)}****${bare.slice(-2)}`;
  }
  if (bare.length <= 4) return `ID ${bare[0]}***`;
  return `ID ${bare.slice(0, 2)}****${bare.slice(-2)}`;
}

function formatPoints(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? 0);
  return Math.round(num).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}


function formatGrouped(value) {
  const num = Number(String(value ?? '').replace(/\s/g, ''));
  if (!Number.isFinite(num)) return String(value ?? '');
  return Math.round(num).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function parsePlacesCount(promo) {
  const raw = String(promo?.places || '').replace(/\s/g, '');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 300;
}

const FAKE_TOP = [
  { rank: 1, participant: '31000004', points: 132830, bonus: '2 000 TL', bonusFs: '55 FS' },
  { rank: 2, participant: '80000029', points: 59590, bonus: '1 500 TL', bonusFs: '55 FS' },
  { rank: 3, participant: '81000071', points: 58670, bonus: '1 000 TL', bonusFs: '55 FS' },
];

const FAKE_RANGES = [
  { place: '4...5', fromPoints: 31930, cash: '600 TL', fs: '55 FS' },
  { place: '6...10', fromPoints: 22270, cash: '350 TL', fs: '55 FS' },
  { place: '11...20', fromPoints: 15840, cash: '200 TL', fs: '55 FS' },
  { place: '21...50', fromPoints: 10420, cash: '120 TL', fs: '30 FS' },
  { place: '51...100', fromPoints: 7310, cash: '80 TL', fs: '20 FS' },
  { place: '101...150', fromPoints: 4850, cash: '50 TL', fs: '15 FS' },
  { place: '151...200', fromPoints: 3120, cash: '30 TL', fs: '10 FS' },
  { place: '201...250', fromPoints: 1840, cash: '20 TL', fs: '10 FS' },
  { place: '251...300', fromPoints: 900, cash: '10 TL', fs: '5 FS' },
];

/** Fonbet rating: top-3 participants (hex) + remaining place ranges with prizes. */
function buildRatingRows(leaderboard, promo) {
  const explicit = promo?.prizeTiers || promo?.placesList || promo?.placePrizes;
  if (Array.isArray(explicit) && explicit.length) {
    return explicit.map((row, idx) => {
      const isRange = Boolean(row.range || row.place?.includes?.('...') || row.kind === 'range');
      if (isRange) {
        return {
          key: `tier-${idx}`,
          type: 'range',
          place: row.place || row.range || row.label,
          fromPoints: row.fromPoints ?? row.from ?? row.points ?? 0,
          cash: row.cash || row.bonus || row.prize || '',
          fs: row.fs || row.bonusFs || '',
        };
      }
      return {
        key: `tier-${idx}`,
        type: 'player',
        rank: row.rank || idx + 1,
        participant: row.participant || row.name || row.user || String(31000000 + idx),
        points: row.points ?? row.fromPoints ?? 0,
        cash: row.cash || row.bonus || '',
        fs: row.fs || row.bonusFs || '',
      };
    });
  }

  const source = (Array.isArray(leaderboard) && leaderboard.length ? leaderboard : FAKE_TOP)
    .slice()
    .sort((a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0) || (Number(b.points) || 0) - (Number(a.points) || 0));

  const top = [];
  for (let i = 0; i < 3; i += 1) {
    const src = source[i] || FAKE_TOP[i];
    const parts = parseLeaderboardBonus(src);
    top.push({
      key: `player-${i + 1}`,
      type: 'player',
      rank: i + 1,
      participant: src.participant || src.name || src.user || FAKE_TOP[i].participant,
      points: src.points ?? FAKE_TOP[i].points,
      cash: parts.cash && parts.cash !== '—' ? parts.cash : (src.bonus || FAKE_TOP[i].bonus),
      fs: parts.fs || src.bonusFs || FAKE_TOP[i].bonusFs,
    });
  }

  const total = parsePlacesCount(promo);
  const ranges = FAKE_RANGES
    .map((row) => {
      const m = String(row.place).match(/(\d+)\.\.\.(\d+)/);
      if (!m) return { ...row, key: `range-${row.place}`, type: 'range' };
      const start = Number(m[1]);
      if (start > total) return null;
      const end = Math.min(Number(m[2]), total);
      return {
        key: `range-${start}-${end}`,
        type: 'range',
        place: start === end ? String(start) : `${start}...${end}`,
        fromPoints: row.fromPoints,
        cash: row.cash,
        fs: row.fs,
      };
    })
    .filter(Boolean);

  return [...top, ...ranges];
}

function resolveMeta(promo, leaderboard, timeLeft, locale, liveParticipants = null) {
  const live = liveParticipants != null && Number.isFinite(Number(liveParticipants))
    ? formatGrouped(Math.max(0, Math.round(Number(liveParticipants))))
    : '';
  const participants = live
    || String(promo?.participants || '').trim()
    || (leaderboard.length ? formatGrouped(Math.max(leaderboard.length * 37 + 198, 4198)) : '4 198');
  const places = String(promo?.places || '').trim() || formatGrouped(parsePlacesCount(promo));
  const days = String(timeLeft || '').trim()
    || String(promo?.daysLeft || promo?.timer || '').trim()
    || (locale === 'en' ? '3 days' : '3 gün');
  const prizeMain = String(promo?.prizeMain || '').trim()
    || (!promo?.name ? String(promo?.title || '').replace(/<br\s*\/?>/gi, ' ') : '')
    || '300 000 TL';
  const prizeSub = String(promo?.prizeSub || '').trim() || '+ 15 000 FS';
  return { participants, places, days, prizeMain, prizeSub };
}

function normalizeGames(promo) {
  const list = promo?.games || promo?.eligibleGames || [];
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.map((g, idx) => {
    if (typeof g === 'string') {
      return { id: `g-${idx}`, name: g, image: '', provider: '' };
    }
    const id = String(g.id || g.gameId || g.game_id || `g-${idx}`);
    const name = g.name || g.title || g.game_name || '—';
    const key = `${id}:${name}`.toLowerCase();
    if (seen.has(key)) return null;
    seen.add(key);
    return {
      id,
      name,
      image: g.image || g.img || g.game_image || g.icon_url || g.iconUrl || '',
      provider: g.provider || g.vendor || g.providerName || '',
    };
  }).filter(Boolean);
}

function asTermSection(raw, idx) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    return { key: `s-${idx}`, title: '', items: [text] };
  }
  if (typeof raw !== 'object') return null;
  const title = String(raw.title || raw.heading || raw.name || '').trim();
  let items = [];
  if (Array.isArray(raw.items)) items = raw.items;
  else if (Array.isArray(raw.bullets)) items = raw.bullets;
  else if (Array.isArray(raw.points)) items = raw.points;
  else if (raw.body || raw.text) items = [raw.body || raw.text];
  items = items.map((x) => String(x || '').trim()).filter(Boolean);
  if (!title && !items.length) return null;
  return { key: raw.id || `s-${idx}`, title, items };
}

function normalizeTermSections(promo) {
  const candidates = [
    promo?.termsSections,
    promo?.termSections,
    promo?.terms,
    promo?.rules,
  ];
  for (const list of candidates) {
    if (!Array.isArray(list) || !list.length) continue;
    const sections = list.map(asTermSection).filter(Boolean);
    if (sections.length) return sections;
  }
  return [];
}

function formatPromoPeriod(promo, locale) {
  const start = promo?.startsAt || promo?.starts_at;
  const end = promo?.endsAt || promo?.ends_at;
  if (!start && !end) return '';
  const loc = locale === 'en' ? 'en-GB' : 'tr-TR';
  const fmt = (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(loc, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const a = start ? fmt(start) : '';
  const b = end ? fmt(end) : '';
  if (a && b) {
    return locale === 'en'
      ? `From ${a} to ${b}.`
      : `${a} — ${b} tarihleri arasında.`;
  }
  return a || b;
}

function defaultTermSections(promo, locale, tr) {
  const period = formatPromoPeriod(promo, locale)
    || (locale === 'en'
      ? 'See the promotion page for the tournament period.'
      : 'Turnuva dönemi için promosyon sayfasına bakın.');
  return [
    {
      key: 'period',
      title: tr('promos.tournament.terms.periodTitle'),
      items: [period],
    },
    {
      key: 'points',
      title: tr('promos.tournament.terms.pointsTitle'),
      items: [
        tr('promos.tournament.terms.points1'),
        tr('promos.tournament.terms.points2'),
        tr('promos.tournament.terms.points3'),
      ],
    },
    {
      key: 'bonuses',
      title: tr('promos.tournament.terms.bonusesTitle'),
      items: [
        tr('promos.tournament.terms.bonuses1'),
        tr('promos.tournament.terms.bonuses2'),
        tr('promos.tournament.terms.bonuses3'),
      ],
    },
  ];
}

function MetaBox({ icon: Icon, label, value }) {
  return (
    <div className="tournament-dialog__meta-box">
      <div className="tournament-dialog__meta-box-label">{label}</div>
      <div className="tournament-dialog__meta-box-row">
        <span className="tournament-dialog__meta-box-icon"><Icon /></span>
        <span className="tournament-dialog__meta-box-value">{value || '—'}</span>
      </div>
    </div>
  );
}

function useTournamentCountdown(promo, locale) {
  const [now, setNow] = useState(() => Date.now());
  const end = getTournamentEndMs(promo);
  const start = getTournamentStartMs(promo);
  const target = (() => {
    const phase = getTournamentPhase(promo, now);
    if (phase === 'upcoming') return start;
    return end;
  })();

  useEffect(() => {
    if (target == null) return undefined;
    const tick = () => setNow(Date.now());
    tick();
    const remaining = target - Date.now();
    const intervalMs = remaining > 86400000 ? 60000 : 1000;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [target]);

  const live = formatTournamentCountdown(promo, now, locale);
  if (live) return live;
  return String(promo?.daysLeft || promo?.timer || '').trim() || '—';
}

function BonusStack({ cash, fs }) {
  return (
    <div className="tournament-dialog__bonus-col">
      {cash ? (
        <div className="tournament-dialog__bonus-line">
          <span className="tournament-dialog__bonus">{cash}</span>
          <span className="tournament-dialog__bonus-ico tournament-dialog__bonus-ico--gift" aria-hidden="true">
            <IconGift />
          </span>
        </div>
      ) : null}
      {fs ? (
        <div className="tournament-dialog__bonus-line">
          <span className="tournament-dialog__bonus-fs">{fs}</span>
          <span className="tournament-dialog__bonus-ico tournament-dialog__bonus-ico--star" aria-hidden="true">
            <IconFsStar />
          </span>
        </div>
      ) : null}
    </div>
  );
}

function RatingRow({ row, fromLabel }) {
  if (row.type === 'range') {
    return (
      <li className="tournament-dialog__row tournament-dialog__row--range">
        <div className="tournament-dialog__rank-slot">
          <span className="tournament-dialog__range-place">{row.place}</span>
        </div>
        <div className="tournament-dialog__mid">
          <span className="tournament-dialog__from-label">{fromLabel}</span>
          <span className="tournament-dialog__points">
            <span className="tournament-dialog__points-coin" aria-hidden="true"><IconCoin size={14} /></span>
            <span className="tournament-dialog__points-val">{formatGrouped(row.fromPoints)}</span>
          </span>
        </div>
        <BonusStack cash={row.cash} fs={row.fs} />
      </li>
    );
  }

  return (
    <li className={`tournament-dialog__row tournament-dialog__row--player tournament-dialog__row--rank-${row.rank}`}>
      <div className="tournament-dialog__rank-slot">
        <RankHexagon rank={row.rank} />
      </div>
      <div className="tournament-dialog__mid">
        <span className="tournament-dialog__user">{maskParticipant(row.participant)}</span>
        <span className="tournament-dialog__points">
          <span className="tournament-dialog__points-coin" aria-hidden="true"><IconCoin size={14} /></span>
          <span className="tournament-dialog__points-val">{formatPoints(row.points)}</span>
        </span>
      </div>
      <BonusStack cash={row.cash} fs={row.fs} />
    </li>
  );
}


const TOURNAMENT_FAQ_KEYS = [
  { q: 'promos.tournament.faq.participate.q', a: 'promos.tournament.faq.participate.a' },
  { q: 'promos.tournament.faq.deposit.q', a: 'promos.tournament.faq.deposit.a' },
  { q: 'promos.tournament.faq.multiple.q', a: 'promos.tournament.faq.multiple.a' },
  { q: 'promos.tournament.faq.terms.q', a: 'promos.tournament.faq.terms.a' },
  { q: 'promos.tournament.faq.games.q', a: 'promos.tournament.faq.games.a' },
  { q: 'promos.tournament.faq.bonuses.q', a: 'promos.tournament.faq.bonuses.a' },
  { q: 'promos.tournament.faq.timed.q', a: 'promos.tournament.faq.timed.a' },
  { q: 'promos.tournament.faq.track.q', a: 'promos.tournament.faq.track.a' },
  { q: 'promos.tournament.faq.more.q', a: 'promos.tournament.faq.more.a' },
];

export default function TournamentModal({ promo, onClose, onRegister }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale || 'tr';
  const { user, token } = useAuth();
  const [phase, setPhase] = useState('enter');
  const [tab, setTab] = useState('rating');
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [participantsCount, setParticipantsCount] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(true);
  const [showRules, setShowRules] = useState(false);
  const [showAllGames, setShowAllGames] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [askText, setAskText] = useState('');
  const [askFiles, setAskFiles] = useState([]);
  const [askBusy, setAskBusy] = useState(false);
  const [askMsg, setAskMsg] = useState('');

  const promoKey = promo?.promoId || promo?.id;
  const banner = promo?.bannerImg || promo?.heroImg || promo?.img;
  const title = promo?.name || String(promo?.title || '').replace(/<br\s*\/?>/gi, ' ') || promo?.detailTitle || promo?.caption || 'Turnuva';
  const pageHtml = getPromoPageHtml(promo);
  const termSections = useMemo(() => {
    const fromPromo = normalizeTermSections(promo);
    if (fromPromo.length) return fromPromo;
    return defaultTermSections(promo, locale, tr);
  }, [promo, locale, tr]);
  const games = useMemo(() => normalizeGames(promo), [promo]);
  const timeLeft = useTournamentCountdown(promo, locale);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const tournamentPhase = getTournamentPhase(promo, nowTick);
  const isUpcoming = tournamentPhase === 'upcoming';
  const isEnded = tournamentPhase === 'ended';
  const clockLabel = isUpcoming
    ? tr('promos.tournament.untilStart')
    : tr('promos.tournament.untilCompletion');
  const ratingRows = useMemo(() => buildRatingRows(leaderboard, promo), [leaderboard, promo]);
  const meta = useMemo(
    () => resolveMeta(promo, leaderboard, timeLeft, locale, participantsCount),
    [promo, leaderboard, timeLeft, locale, participantsCount],
  );
  const fromLabel = locale === 'en' ? 'from' : 'itibaren';

  const loadBoard = useCallback(async ({ silent = false } = {}) => {
    if (!promoKey) return;
    if (!silent) setLoadingBoard(true);
    try {
      const data = await api.tournamentLeaderboard(promoKey, token);
      let rows = Array.isArray(data?.leaderboard) ? data.leaderboard : [];
      if (!rows.length && Array.isArray(promo?.leaderboard)) rows = promo.leaderboard;
      setLeaderboard(rows.map((row, idx) => {
        const parts = parseLeaderboardBonus(row);
        return {
          rank: row.rank || idx + 1,
          participant: row.participant || row.name || row.user,
          points: row.points ?? row.score ?? 0,
          bonus: parts.cash,
          bonusFs: parts.fs,
        };
      }));
      if (data?.participants != null && Number.isFinite(Number(data.participants))) {
        setParticipantsCount(Number(data.participants));
      }
      setJoined(Boolean(data?.joined));
    } catch {
      if (!silent) {
        const seeded = Array.isArray(promo?.leaderboard) ? promo.leaderboard : [];
        setLeaderboard(seeded.map((row, idx) => {
          const parts = parseLeaderboardBonus(row);
          return {
            rank: row.rank || idx + 1,
            participant: row.participant || row.name || row.user,
            points: row.points ?? row.score ?? 0,
            bonus: parts.cash,
            bonusFs: parts.fs,
          };
        }));
        setJoined(false);
      }
    } finally {
      if (!silent) setLoadingBoard(false);
    }
  }, [promoKey, token, promo]);

  useEffect(() => {
    if (!promo) return undefined;
    setPhase('enter');
    setTab('rating');
    setShowRules(false);
    setShowAllGames(false);
    setShowFaq(false);
    setShowAsk(false);
    setAskText('');
    setAskFiles([]);
    setAskBusy(false);
    setAskMsg('');
    setMsg('');
    const openTimer = window.requestAnimationFrame(() => setPhase('open'));
    loadBoard();
    return () => window.cancelAnimationFrame(openTimer);
  }, [promo, loadBoard]);

  useEffect(() => {
    if (!promoKey) return undefined;
    const id = window.setInterval(() => {
      loadBoard({ silent: true });
    }, 12000);
    return () => window.clearInterval(id);
  }, [promoKey, loadBoard]);


  const handleClose = useCallback(() => {
    setPhase('leave');
    window.setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const toggleJoin = async () => {
    if (busy) return;
    if (!token) {
      onRegister?.();
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      if (joined) {
        await api.tournamentLeave(token, promoKey);
        setJoined(false);
        setMsg(tr('promos.tournament.leftOk'));
      } else {
        await api.tournamentJoin(token, promoKey);
        setJoined(true);
        setMsg(tr('promos.tournament.joinedOk'));
      }
      await loadBoard();
    } catch (err) {
      setMsg(err?.message || tr('promos.tournament.actionError'));
    } finally {
      setBusy(false);
    }
  };

  if (!promo) return null;

  const tabs = [
    { id: 'rating', label: tr('promos.tournament.tab.rating') },
    { id: 'terms', label: tr('promos.tournament.tab.terms') },
    { id: 'games', label: tr('promos.tournament.tab.games') },
  ];

  return createPortal(
    <div
      className={`promo-dialog tournament-dialog tournament-dialog--fonbet${phase === 'open' ? ' is-open' : ''}${phase === 'leave' ? ' is-closing' : ''}${isUpcoming ? ' is-upcoming' : ''}${isEnded ? ' is-ended' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tournament-dialog-title"
    >
      <div className="promo-dialog__overlay" onClick={handleClose} />
      <div className="promo-dialog__window tournament-dialog__window">
        <div className="promo-dialog__engine is-ready">
          <header className="tournament-dialog__topbar">
            <span className="tournament-dialog__top-spacer" />
            <h2 id="tournament-dialog-title" className="tournament-dialog__top-title">{title}</h2>
            <button
              type="button"
              className="tournament-dialog__top-btn"
              aria-label={tr('promos.tournament.close')}
              onClick={handleClose}
            >
              <IconClose />
            </button>
          </header>

          <div className="promo-dialog__grid is-visible">
            <div className="promo-dialog__scroll-outer">
              <div className="promo-dialog__scroll">
                <div className="promo-dialog__content">
                  <div
                    className="tournament-dialog__hero"
                    style={banner ? { backgroundImage: `url(${banner})` } : undefined}
                  >
                    <div className="tournament-dialog__hero-shade" />
                    {isUpcoming ? (
                      <div className="tournament-dialog__upcoming-veil">
                        <div className="tournament-dialog__upcoming-badge">{tr('promos.tournament.upcoming')}</div>
                        <div className="tournament-dialog__upcoming-count">{timeLeft}</div>
                        <div className="tournament-dialog__upcoming-label">{tr('promos.tournament.untilStart')}</div>
                      </div>
                    ) : null}
                    <div className="tournament-dialog__hero-content">
                      <div className="tournament-dialog__fund-label">{tr('promos.tournament.bonusFund')}</div>
                      <div className="tournament-dialog__fund-main">{meta.prizeMain}</div>
                      {meta.prizeSub ? <div className="tournament-dialog__fund-sub">{meta.prizeSub}</div> : null}
                      <div className="tournament-dialog__meta">
                        {!isUpcoming ? (
                          <>
                            <MetaBox icon={IconParticipants} label={tr('promos.tournament.participants')} value={meta.participants} />
                            <MetaBox icon={IconPlaces} label={tr('promos.tournament.places')} value={meta.places} />
                          </>
                        ) : null}
                        <MetaBox icon={IconClock} label={clockLabel} value={timeLeft || meta.days} />
                      </div>
                    </div>
                  </div>

                  <div className="promo-dialog__body tournament-dialog__body">
                    <div className="tournament-dialog__tabs" role="tablist">
                      {tabs.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          role="tab"
                          aria-selected={tab === item.id}
                          className={`tournament-dialog__tab${tab === item.id ? ' is-active' : ''}`}
                          onClick={() => setTab(item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    {tab === 'rating' && (
                      <div className="tournament-dialog__panel">
                        {isUpcoming ? (
                          <p className="tournament-dialog__empty">
                            {locale === 'en'
                              ? 'The leaderboard will appear when the tournament starts.'
                              : 'Sıralama turnuva başladığında görünecek.'}
                          </p>
                        ) : (
                          <>
                            <div className="tournament-dialog__table-head">
                              <span>{tr('promos.tournament.col.participant')}</span>
                              <span>{tr('promos.tournament.col.bonuses')}</span>
                            </div>
                            {loadingBoard ? (
                              <p className="tournament-dialog__empty">{tr('promos.tournament.loading')}</p>
                            ) : (
                              <ul className="tournament-dialog__table">
                                {ratingRows.map((row) => (
                                  <RatingRow key={row.key} row={row} fromLabel={fromLabel} />
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {tab === 'terms' && (
                      <div className="tournament-dialog__panel tournament-dialog__panel--terms">
                        {showRules && pageHtml ? (
                          <div className="promo-dialog__rules-body" dangerouslySetInnerHTML={{ __html: pageHtml }} />
                        ) : termSections.length ? (
                          <div className="tournament-dialog__terms">
                            {termSections.map((section) => (
                              <section key={section.key} className="tournament-dialog__terms-section">
                                {section.title ? (
                                  <h3 className="tournament-dialog__terms-title">{section.title}</h3>
                                ) : null}
                                {section.items?.length ? (
                                  <ul className="tournament-dialog__terms-list">
                                    {section.items.map((item, i) => (
                                      <li key={`${section.key}-${i}`} className="tournament-dialog__terms-item">
                                        <span className="tournament-dialog__terms-bullet" aria-hidden="true">•</span>
                                        <span
                                          className="tournament-dialog__terms-text"
                                          dangerouslySetInnerHTML={{ __html: String(item).replace(/\n/g, '<br>') }}
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </section>
                            ))}
                          </div>
                        ) : pageHtml ? (
                          <div className="promo-dialog__rules-body" dangerouslySetInnerHTML={{ __html: pageHtml }} />
                        ) : (
                          <p className="tournament-dialog__empty">{tr('promos.tournament.noTerms')}</p>
                        )}
                      </div>
                    )}

                    {tab === 'games' && (
                      <div className="tournament-dialog__panel tournament-dialog__panel--games">
                        {games.length ? (
                          <ul className="tournament-dialog__games">
                            {(showAllGames ? games : games.slice(0, 9)).map((game) => (
                              <li key={game.id} className="tournament-dialog__game">
                                <div className="tournament-dialog__game-media">
                                  <span className="tournament-dialog__game-badge">
                                    {tr('promos.tournament.gameBadge')}
                                  </span>
                                  {game.image ? (
                                    <span className="tournament-dialog__game-img" style={{ backgroundImage: `url(${game.image})` }} />
                                  ) : (
                                    <span className="tournament-dialog__game-img tournament-dialog__game-img--empty" />
                                  )}
                                  <div className="tournament-dialog__game-meta">
                                    <span className="tournament-dialog__game-name">{game.name}</span>
                                    {game.provider ? (
                                      <span className="tournament-dialog__game-provider">{game.provider}</span>
                                    ) : null}
                                  </div>
                                </div>
                              </li>
                            ))}
                            {!showAllGames && games.length > 9 ? (
                              <li className="tournament-dialog__game">
                                <button
                                  type="button"
                                  className="tournament-dialog__game--more"
                                  onClick={() => setShowAllGames(true)}
                                >
                                  <span className="tournament-dialog__game-more-inner">
                                    {tr('promos.tournament.showAllGames')}
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 17" aria-hidden="true">
                                      <path fill="currentColor" fillRule="evenodd" d="M8.549 2.971A.55.55 0 0 0 8 2.5a.55.55 0 0 0-.553.545v9.588L3.946 9.17l-.062-.053a.56.56 0 0 0-.721.051.54.54 0 0 0 .051.732l4.5 4.118.053.043a.55.55 0 0 0 .71-.043l4.5-4.118a.54.54 0 0 0 .05-.732.56.56 0 0 0-.72-.05l-.063.052-3.501 3.464V3.516Z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                </button>
                              </li>
                            ) : null}
                          </ul>
                        ) : (
                          <p className="tournament-dialog__empty">{tr('promos.tournament.noGames')}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tournament-dialog__footer">
            <button
              type="button"
              className={`tournament-dialog__join${joined ? ' is-joined' : ''}${isUpcoming ? ' is-upcoming' : ''}`}
              disabled={busy || isEnded}
              onClick={toggleJoin}
            >
              {busy
                ? tr('promos.tournament.working')
                : isEnded
                  ? (locale === 'en' ? 'Ended' : 'Sona erdi')
                  : joined
                    ? tr('promos.tournament.optOut')
                    : (user
                      ? (isUpcoming
                        ? (locale === 'en' ? 'Auto-join next tournament' : 'Sonraki turnuvaya otomatik katıl')
                        : tr('promos.tournament.participate'))
                      : tr('promos.tournament.loginToJoin'))}
            </button>
            <div className="tournament-dialog__footer-links">
              <button
                type="button"
                className="tournament-dialog__link-btn"
                onClick={() => {
                  setTab('terms');
                  setShowRules(true);
                  setShowFaq(false);
                }}
              >
                {tr('promos.tournament.promoRules')}
              </button>
              <button
                type="button"
                className="tournament-dialog__link-btn"
                onClick={() => setShowFaq(true)}
              >
                {tr('promos.tournament.questions')}
              </button>
            </div>
            {msg ? <p className="tournament-dialog__msg">{msg}</p> : null}
          </div>

          <div className={`tournament-dialog__faq${showFaq ? ' is-open' : ''}`} aria-hidden={!showFaq}>
            <div className="tournament-dialog__faq-panel" role="dialog" aria-modal="true" aria-labelledby="tournament-faq-title">
              <div className="tournament-dialog__faq-thumb" aria-hidden="true" />
              <div className="tournament-dialog__faq-head">
                <h3 id="tournament-faq-title" className="tournament-dialog__faq-title">{tr('promos.tournament.questions')}</h3>
                <button
                  type="button"
                  className="tournament-dialog__faq-close"
                  aria-label={tr('promos.tournament.close')}
                  onClick={() => setShowFaq(false)}
                >
                  <IconClose />
                </button>
              </div>
              <div className="tournament-dialog__faq-scroll">
                <div className="tournament-dialog__faq-list">
                  {TOURNAMENT_FAQ_KEYS.map((item) => (
                    <details key={item.q} className="tournament-dialog__faq-item">
                      <summary className="tournament-dialog__faq-q">{tr(item.q)}</summary>
                      <div className="tournament-dialog__faq-a">{tr(item.a)}</div>
                    </details>
                  ))}
                </div>
              </div>
              <div className="tournament-dialog__faq-footer">
                <button
                  type="button"
                  className="tournament-dialog__link-btn tournament-dialog__faq-rules"
                  onClick={() => {
                    setShowFaq(false);
                    setTab('terms');
                    setShowRules(true);
                  }}
                >
                  {tr('promos.tournament.promoRules')}
                </button>
                <button
                  type="button"
                  className="tournament-dialog__faq-cta"
                  onClick={() => {
                    setAskMsg('');
                    setShowAsk(true);
                  }}
                >
                  {tr('promos.tournament.askQuestion')}
                </button>
                <button
                  type="button"
                  className="tournament-dialog__faq-secondary"
                  onClick={() => setShowFaq(false)}
                >
                  {tr('promos.tournament.close')}
                </button>
              </div>
            </div>
          </div>

          <div className={`tournament-dialog__ask${showAsk ? ' is-open' : ''}`} aria-hidden={!showAsk}>
            <div className="tournament-dialog__ask-panel" role="dialog" aria-modal="true" aria-labelledby="tournament-ask-title">
              <div className="tournament-dialog__faq-thumb" aria-hidden="true" />
              <div className="tournament-dialog__ask-head">
                <div className="tournament-dialog__ask-head-text">
                  <div id="tournament-ask-title" className="tournament-dialog__ask-title">{tr('promos.tournament.ask.title')}</div>
                  <div className="tournament-dialog__ask-sub">{tr('promos.tournament.ask.subtitle')}</div>
                </div>
                <button
                  type="button"
                  className="tournament-dialog__faq-close"
                  aria-label={tr('promos.tournament.close')}
                  onClick={() => setShowAsk(false)}
                >
                  <IconClose />
                </button>
              </div>
              <div className="tournament-dialog__ask-body">
                <div className="tournament-dialog__ask-field">
                  <textarea
                    className="tournament-dialog__ask-textarea"
                    maxLength={1500}
                    rows={5}
                    placeholder={tr('promos.tournament.ask.placeholder')}
                    value={askText}
                    onChange={(e) => setAskText(e.target.value)}
                  />
                  <div className="tournament-dialog__ask-chars">
                    {tr('promos.tournament.ask.charsLeft').replace('{count}', String(Math.max(0, 1500 - askText.length)))}
                  </div>
                </div>
                <div className="tournament-dialog__ask-actions">
                  <label className="tournament-dialog__ask-attach">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      multiple
                      hidden
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        const next = [...askFiles, ...picked]
                          .filter((f) => f.size <= 7 * 1024 * 1024)
                          .slice(0, 5);
                        setAskFiles(next);
                        e.target.value = '';
                      }}
                    />
                    <span>{tr('promos.tournament.ask.attach')}</span>
                  </label>
                  <div className="tournament-dialog__ask-hint">{tr('promos.tournament.ask.attachHint')}</div>
                  {askFiles.length ? (
                    <ul className="tournament-dialog__ask-files">
                      {askFiles.map((f, idx) => (
                        <li key={`${f.name}-${idx}`}>
                          <span>{f.name}</span>
                          <button type="button" onClick={() => setAskFiles((prev) => prev.filter((_, i) => i !== idx))}>×</button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {askMsg ? <p className="tournament-dialog__ask-msg">{askMsg}</p> : null}
                  <button
                    type="button"
                    className="tournament-dialog__faq-cta"
                    disabled={askBusy || !askText.trim()}
                    onClick={async () => {
                      if (!token) {
                        setAskMsg(tr('promos.tournament.ask.loginRequired'));
                        onRegister?.();
                        return;
                      }
                      setAskBusy(true);
                      setAskMsg('');
                      try {
                        const names = askFiles.map((f) => f.name);
                        const message = names.length
                          ? `${askText.trim()}\n\n[${names.join(', ')}]`
                          : askText.trim();
                        await api.createSupportTicket(token, {
                          type_key: 'bonus',
                          subject: tr('promos.tournament.ask.subtitle'),
                          message,
                          locale,
                          payload: {
                            source: 'tournament',
                            promoId: promoKey,
                            attachments: names,
                          },
                        });
                        setAskMsg(tr('promos.tournament.ask.sent'));
                        setAskText('');
                        setAskFiles([]);
                      } catch (err) {
                        setAskMsg(err?.message || tr('promos.tournament.ask.error'));
                      } finally {
                        setAskBusy(false);
                      }
                    }}
                  >
                    {askBusy ? tr('promos.tournament.ask.sending') : tr('promos.tournament.ask.send')}
                  </button>
                  <button
                    type="button"
                    className="tournament-dialog__faq-secondary"
                    onClick={() => setShowAsk(false)}
                  >
                    {tr('promos.tournament.ask.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
